import { useCallback, useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";

import api, { tokens } from "../../services/api";
import { signOut } from "../../services/auth";
import { useUser } from "../../hook/useUser";
import { notify } from "../../components/ui/Toast";
import { Confirm } from "../../components/ui/panelKit";
import { readApiError } from "../../utils/apiError";
import { describeEvent } from "../../utils/auditText";
import { initials } from "../../utils/media";
import { exactTime, relativeTime } from "../../utils/time";
import { shortAgent } from "../../utils/userAgent";

const ROLE_LABELS = { staff: "Ichki xodim", client: "Mijoz" };

const when = (t) =>
  t ? (
    <span title={exactTime(t)} style={{ whiteSpace: "nowrap" }}>
      {relativeTime(t)}
    </span>
  ) : (
    "—"
  );

/**
 * Your own account (rebuilt 2026-10-22).
 *
 * Still read-only where it was: name and PINFL arrive from OneID with the
 * session and this panel cannot change them -- the page says so rather than
 * drawing disabled inputs. The PINFL stays prominent, because every grant in
 * this system is keyed on it.
 *
 * New: your numbers and recent activity from the admin audit log, your
 * recent sign-ins, and one safety action -- sign out of every other device
 * while staying signed in here (POST /me/end-other-sessions/).
 */
const ProfilePage = () => {
  const { user, loading } = useUser();
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState(null);
  const [logins, setLogins] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!user?.id) return;
    const base = `/users/${user.id}`;
    api.get(`${base}/`).then((r) => setStats(r.data)).catch(() => setStats({}));
    api
      .get(`${base}/activity/`, { params: { limit: 6 } })
      .then((r) => setActivity(r.data.results || []))
      .catch(() => setActivity([]));
    api
      .get(`${base}/logins/`, { params: { limit: 5 } })
      .then((r) => setLogins(r.data.results || []))
      .catch(() => setLogins([]));
  }, [user?.id]);

  useEffect(() => {
    load();
  }, [load]);

  const endOthers = async () => {
    setBusy(true);
    try {
      const res = await api.post("/me/end-other-sessions/", { refresh: tokens.refresh });
      notify.success(
        res.data.ended
          ? `Boshqa ${res.data.ended} ta qurilmadan chiqildi. Bu yerda qolasiz.`
          : "Boshqa faol qurilma yoʻq edi.",
      );
      setConfirm(false);
      load();
    } catch (err) {
      notify.error(readApiError(err, {}, "Bajarib boʻlmadi."));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <div className="ms-spinner" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="ms-panel">
        <div className="ms-state">
          <Icon icon="tabler:alert-triangle" />
          <span>Hisob maʼlumotlarini oʻqib boʻlmadi.</span>
        </div>
      </div>
    );
  }

  const name = user.full_name || user.email || "Foydalanuvchi";
  const others = stats?.session_count ? Math.max(0, stats.session_count - 1) : 0;

  return (
    <>
      <section className="ms-hero">
        <div className="ms-hero-avatar">{initials(name)}</div>
        <div className="ms-hero-who">
          <h1>{name}</h1>
          <div className="ms-hero-chips">
            <span className="ms-hero-chip">
              <Icon icon="tabler:id" />
              <span className="ms-mono">{user.pinfl || "PINFL yoʻq"}</span>
            </span>
            <span className="ms-hero-chip">
              <Icon icon="tabler:shield-check" />
              {ROLE_LABELS[user.role] || user.role}
            </span>
            <span className="ms-hero-chip">
              <Icon icon="tabler:circle-check" />
              OneID orqali tasdiqlangan
            </span>
          </div>
        </div>
        <div className="ms-hero-actions">
          <button type="button" className="ms-btn ms-btn--hero" onClick={signOut}>
            <Icon icon="tabler:power" />
            Chiqish
          </button>
        </div>
      </section>

      <div className="ms-dash-kpis">
        <div className="ms-dkpi">
          <div className="ms-dkpi-top">
            <span>Amallarim</span>
            <Icon icon="tabler:activity" />
          </div>
          <div className="ms-dkpi-value">{stats?.action_count ?? "—"}</div>
          <div className="ms-dkpi-foot">panel va kabinetda, 22-oktabrdan</div>
        </div>
        <div className="ms-dkpi">
          <div className="ms-dkpi-top">
            <span>Kirishlarim</span>
            <Icon icon="tabler:login-2" />
          </div>
          <div className="ms-dkpi-value">{stats?.login_count ?? "—"}</div>
          <div className="ms-dkpi-foot">
            oxirgisi {user.last_oneid_login_at ? relativeTime(user.last_oneid_login_at) : "—"}
          </div>
        </div>
        <div className="ms-dkpi">
          <div className="ms-dkpi-top">
            <span>Faol qurilmalar</span>
            <Icon icon="tabler:devices" />
          </div>
          <div className="ms-dkpi-value">{stats?.session_count ?? "—"}</div>
          <div className="ms-dkpi-foot">shu brauzer bilan birga</div>
        </div>
      </div>

      <div className="ms-dash-row ms-dash-row--half">
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:user-circle" />
              <h2>Hisob maʼlumotlari</h2>
            </div>
            <dl className="ms-facts">
              <div>
                <dt>Toʻliq ism</dt>
                <dd>{user.full_name || "—"}</dd>
              </div>
              <div>
                <dt>PINFL</dt>
                <dd className="ms-mono">{user.pinfl || "—"}</dd>
              </div>
              <div>
                <dt>Elektron pochta</dt>
                <dd>{user.email || "—"}</dd>
              </div>
              <div>
                <dt>Rol</dt>
                <dd>{ROLE_LABELS[user.role] || user.role}</dd>
              </div>
              <div>
                <dt>Oxirgi OneID kirishi</dt>
                <dd>{user.last_oneid_login_at ? when(user.last_oneid_login_at) : "hech qachon"}</dd>
              </div>
            </dl>
            <p className="ms-panel-note">
              Ism va PINFL OneID dan keladi va har kirishda yangilanadi — ularni bu panel
              orqali oʻzgartirib boʻlmaydi. Barcha ruxsatlar PINFL ga bogʻlangan.
            </p>
          </div>

          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:lock" />
              <h2>Xavfsizlik</h2>
            </div>
            <div className="ms-secure">
              <div>
                <b>Boshqa qurilmalardan chiqish</b>
                <span>
                  {others > 0
                    ? `Yana ${others} ta qurilmada tizimga kirgansiz. Hammasidan chiqasiz, shu brauzerda qolasiz.`
                    : "Boshqa qurilmada faol sessiya yoʻq."}
                </span>
              </div>
              <button
                type="button"
                className="ms-btn ms-btn--ghost ms-btn--sm"
                disabled={!others}
                onClick={() => setConfirm(true)}
              >
                <Icon icon="tabler:logout" />
                Chiqish
              </button>
            </div>
          </div>

          {user.memberships?.length > 0 && (
            <div className="ms-panel">
              <div className="ms-panel-head">
                <Icon icon="tabler:server-2" />
                <h2>Mijoz tizimlaridagi ruxsatlar</h2>
              </div>
              <dl className="ms-facts">
                {user.memberships.map((m) => (
                  <div key={m.id}>
                    <dt>{m.client_name}</dt>
                    <dd>
                      <span className="ms-badge ms-badge--neutral">
                        {m.access === "admin" ? "administrator" : "oʻqish"}
                      </span>
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:history" />
              <h2>Soʻnggi amallarim</h2>
              <div className="ms-toolbar-spacer" />
              <Link
                className="ms-btn ms-btn--ghost ms-btn--sm"
                to={`/foydalanuvchilar/${user.role === "staff" ? "panel" : "kabinet"}/${user.id}/faoliyat`}
              >
                Barchasi
                <Icon icon="tabler:chevron-right" />
              </Link>
            </div>
            {activity === null ? (
              <div className="ms-state">
                <div className="ms-spinner" />
              </div>
            ) : activity.length === 0 ? (
              <div className="ms-state">
                <Icon icon="tabler:history-off" />
                <span>Hali amal yozilmagan.</span>
              </div>
            ) : (
              <ul className="ms-timeline">
                {activity.map((e) => {
                  const { icon, text, to } = describeEvent(e);
                  return (
                    <li key={e.id}>
                      <span className="ms-timeline-dot">
                        <Icon icon={icon} />
                      </span>
                      <span className="ms-timeline-body">
                        {to ? (
                          <Link className="ms-sublink" to={to}>
                            {text}
                          </Link>
                        ) : (
                          text
                        )}
                        <small>{when(e.created_at)}</small>
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="ms-panel">
            <div className="ms-panel-head">
              <Icon icon="tabler:login-2" />
              <h2>Soʻnggi kirishlar</h2>
            </div>
            {logins === null ? (
              <div className="ms-state">
                <div className="ms-spinner" />
              </div>
            ) : logins.length === 0 ? (
              <div className="ms-state">
                <Icon icon="tabler:login-2" />
                <span>Kirishlar 22-oktabrdan boshlab yoziladi.</span>
              </div>
            ) : (
              <dl className="ms-facts">
                {logins.map((l) => (
                  <div key={l.id}>
                    <dt>
                      {l.data?.method === "hidden" ? "Yashirin kirish" : "OneID"} ·{" "}
                      <span title={l.user_agent}>{shortAgent(l.user_agent)}</span>
                    </dt>
                    <dd>
                      <span className="ms-mono ms-td-muted">{l.ip || "—"}</span> · {when(l.created_at)}
                    </dd>
                  </div>
                ))}
              </dl>
            )}
          </div>
        </div>
      </div>

      <Confirm
        open={confirm}
        busy={busy}
        name={name}
        title="Boshqa qurilmalardan chiqish"
        message="Shu brauzerdan boshqa hamma joyda sessiyangiz tugatiladi; u yerlarda qaytadan OneID orqali kirish kerak boʻladi. Allaqachon berilgan qisqa kalit 15 daqiqagacha ishlashi mumkin."
        confirmLabel="Chiqish"
        cancelLabel="Bekor qilish"
        busyLabel="Bajarilmoqda..."
        onConfirm={endOthers}
        onCancel={() => setConfirm(false)}
      />
    </>
  );
};

export default ProfilePage;
